import { TimerRounded } from '@mui/icons-material';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { formatTime } from '../../../../../Utils/DateUtils';
import BoxCornerIcon from './BoxCornerIcon';
import { baseCardStyling } from './styles';

type MiddleMenuProps = {
  seconds: number;
  isRunning: boolean;
  startTimer: () => void;
  stopTimer: () => void;
};

export const MiddleMenu: FC<MiddleMenuProps> = ({
  seconds,
  startTimer,
  stopTimer,
  isRunning,
}) => {
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: '#202123',
        height: '100%',
        padding: 2,
      })}
    >
      <Stack
        direction="column"
        spacing={2}
        className="w-full"
        justifyContent="flex-end"
        display="flex"
        alignItems="center"
        sx={(theme) => ({
          height: '100%',
          position: 'relative',
        })}
      >
        <Box
          sx={{
            position: 'relative',
            width: '350px',
            height: '100px',
            margin: '0 auto',
            backgroundColor: '#5e5e5eff',
            borderRadius: 4,
            top: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            className="w-full"
            justifyContent="center"
            alignItems="center"
            sx={(theme) => ({
              height: '100%',
              width: '100%',
              padding: 2,
            })}
          >
            <Box
              sx={(theme) => ({
                flex: 1,
                minWidth: 0,
                backgroundColor: '#fff',
                borderRadius: 3,
                height: '100%',
                color: 'darkgrey',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              })}
            >
              <MenuIcon
                fontSize="inherit"
                sx={{
                  fontSize: 42,
                  color: '#393939ff',
                }}
              />
            </Box>
            <Box
              sx={(theme) => ({
                flex: 1,
                minWidth: 0,
                borderRadius: 3,
                height: '100%',
                color: 'grey',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              })}
            >
              <BarChartIcon
                fontSize="inherit"
                sx={{
                  fontSize: 42,
                  color: '#dadadaff',
                }}
              />
            </Box>
            <Box
              sx={(theme) => ({
                flex: 1,
                minWidth: 0,
                borderRadius: 3,
                height: '100%',
                color: 'grey',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              })}
            >
              <DashboardIcon
                fontSize="inherit"
                sx={{
                  fontSize: 42,
                  color: '#dadadaff',
                }}
              />
            </Box>
          </Stack>
        </Box>
        <Box
          sx={{
            ...baseCardStyling,
            position: 'relative',
            backgroundColor: '#36383a',
            height: '100%',
            width: '100%',
          }}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <BoxCornerIcon
            icon={
              <TimerRounded
                fontSize="large"
                sx={{
                  fontSize: 44,
                  color: '#4fcbfcff',
                }}
              />
            }
            left={true}
          />
          {!isRunning ? (
            <GoButton onClick={startTimer} />
          ) : (
            <Stack
              direction="column"
              spacing={2}
              className="w-full"
              justifyContent="space-between"
              alignItems="center"
              sx={(theme) => ({
                height: '100%',
                paddingTop: 22,
                paddingBottom: 10,
              })}
            >
              <Box
                sx={{
                  width: 450,
                  height: 450,
                  borderRadius: '50%',
                  border: '8px solid #5e5e5e',
                  backgroundColor: '#2c2c2cff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                }}
              >
                <Stack
                  direction="column"
                  className="w-full"
                  sx={(theme) => ({
                    display: 'flex', // Enable flex centering
                    alignItems: 'center', // Vertical center
                    justifyContent: 'center', // Horizontal center
                    position: 'relative',
                  })}
                  spacing={2}
                >
                  <Typography
                    sx={(theme) => ({
                      color: 'white',
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                    })}
                  >
                    Time
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      color: '#4fcbfcff',
                      fontSize: '2.4rem',
                      fontWeight: 'bold',
                    })}
                  >
                    min:sec
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      color: 'white',
                      fontSize: '6rem',
                      fontWeight: 'bold',
                    })}
                  >
                    {formatTime(seconds)}
                  </Typography>
                </Stack>
              </Box>
              <StopButton size={200} onClick={stopTimer} />
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

type GoButtonProps = {
  onClick?: () => void;
  size?: number;
  disabled?: boolean;
};

const GoButton: FC<GoButtonProps> = ({
  onClick,
  size = 300,
  disabled = false,
}) => {
  return (
    <Box
      onClick={!disabled ? onClick : undefined}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        // border: '8px solid #4f7351',
        backgroundColor: '#2c2c2cff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.3s ease',
        // boxShadow: '0 8px 20px rgba(76, 175, 80, 0.4)',

        '&:hover': {
          borderColor: '#7bce7fff',
          boxShadow: '0 12px 30px rgba(89, 89, 89, 0.6)',
          transform: 'scale(1.01)',
        },

        '&:active': {
          transform: 'scale(0.98)',
          //   boxShadow: '0 4px 10px rgba(76, 175, 80, 0.4)',
        },
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 'bold',
          color: '#66bb6aff',
          userSelect: 'none',
          fontSize: size > 150 ? '4.6rem' : '3rem', // Responsive text size
        }}
      >
        GO
      </Typography>
    </Box>
  );
};

const StopButton: FC<GoButtonProps> = ({
  onClick,
  size = 300,
  disabled = false,
}) => {
  return (
    <Box
      onClick={!disabled ? onClick : undefined}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        // border: '8px solid #4f7351',
        backgroundColor: '#2c2c2cff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.3s ease',
        // boxShadow: '0 8px 20px rgba(76, 175, 80, 0.4)',

        '&:hover': {
          borderColor: '#7bce7fff',
          boxShadow: '0 12px 30px rgba(89, 89, 89, 0.6)',
          transform: 'scale(1.01)',
        },

        '&:active': {
          transform: 'scale(0.98)',
          //   boxShadow: '0 4px 10px rgba(76, 175, 80, 0.4)',
        },
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 'bold',
          color: '#bb6666ff',
          userSelect: 'none',
          fontSize: '3rem', // Responsive text size
        }}
      >
        STOP
      </Typography>
    </Box>
  );
};
